import * as React from 'react';
import { lazyPopoutCalendarReadingPane } from 'owa-popout-calendar';
import type { CalendarItemPayload } from '../schema';
import { getUserMailboxInfo } from 'owa-client-ids';
import { INVOICE_ENTITY_NAMES_MAP_VALUE } from 'owa-txp-common/lib/helpers/TxpCommonConstants';
import { isFeatureEnabled } from 'owa-feature-flags';
import { toCalendarItem } from 'owa-calendar-event-converter';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import { useGetCalendarEvents } from 'owa-timestream-asset-sdk-calendarevents';
import { useIsMounted } from 'owa-react-hooks/lib/useIsMounted';
import { userDate } from 'owa-datetime';
import {
    AssetCommandDescriptors,
    useAssetCommands,
    usePatchPartialPayload,
} from 'owa-timestream-asset-sdk';
import type { AssetCommonViewProps } from 'owa-timestream-asset-sdk-types';
import { logSpacesDatapoint } from 'owa-timestream-logging';

export default function useCalendarItemViewState({
    payload,
}: AssetCommonViewProps<CalendarItemPayload>) {
    const { isTXPView = true } = payload;
    let { item = {} } = payload;
    const itemId = item.ItemId?.Id;
    const isMounted = useIsMounted();
    const [notFound, setNotFound] = useBooleanState(false);
    const popoutPane = React.useCallback(() => {
        lazyPopoutCalendarReadingPane.importAndExecute(
            {
                Id: itemId as string,
                mailboxInfo: getUserMailboxInfo(),
            },
            'Spaces'
        );
    }, [itemId]);

    const onNotFound = React.useCallback(() => {
        if (isMounted.current) {
            setNotFound();
        }
    }, [isMounted, setNotFound]);

    const { getCalendarEvent } = useGetCalendarEvents();

    const patchPartialPayload = usePatchPartialPayload<CalendarItemPayload>();
    const switchTXPView = React.useCallback(() => {
        logSpacesDatapoint('OUTLOOKSPACES_CAL_TXP_VIEW_SWITCH', { switchToTXPView: !isTXPView });
        patchPartialPayload({ isTXPView: !isTXPView });
    }, [isTXPView]);

    let txpButtonLabel = isTXPView
        ? AssetCommandDescriptors.switchOriginal
        : AssetCommandDescriptors.switchTXP;

    //if isTXPView is false, the fullItem will not be retrieved until the user interacts with the asset (bug #92285)
    const fullItem =
        isMounted.current || isTXPView ? getCalendarEvent(itemId as string, onNotFound) : null;
    let commands = [
        {
            ...AssetCommandDescriptors.openInNewWindow,
            onInvoke: popoutPane,
            disabled: notFound,
        },
        {
            ...txpButtonLabel,
            onInvoke: switchTXPView,
            hidden: !(
                fullItem?.EntityNamesMap &&
                payload.item?.EntityNamesMap != INVOICE_ENTITY_NAMES_MAP_VALUE &&
                isFeatureEnabled('outlookSpaces-txpView')
            ),
        },
    ];

    useAssetCommands(commands, [popoutPane, notFound, fullItem?.EntityNamesMap, isTXPView]);

    // if we have the full item, use the data from it. Otherwise use the defaults we were given
    // Note: only make the call once we're mounted, otherwise the callback has no effect.
    if (fullItem) {
        item = toCalendarItem(fullItem);
    }

    const parsedStart = item.Start ? userDate(item.Start) : '';
    const parsedEnd = item.End ? userDate(item.End) : '';

    return {
        item,
        fullItem,
        itemId,
        popoutPane,
        parsedStart,
        parsedEnd,
        notFound,
        isTXPView,
    };
}
